import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { DatePipe } from '@angular/common';
import * as XLSX from 'xlsx';
import { MatSnackBar } from '@angular/material';
import { animate, state, style, transition, trigger } from '@angular/animations';

export interface Titular {
  cuil_titular: string;
  nombre_titular: string;
  Cuit_Empleador: string;
  Razon_Social: string;
  Periodo: string;
  RemosImp: number;
  Cant_GrupoFamiliar: number;
  Cant_Adherentes: number;
  total_beneficiarios: number;
  // Campos de beneficiarios (titular)
  ben_cuil: string;
  ben_orden: number;
  ben_seccional: number;
  ben_parentesco: number;
  ben_pais: number;
  ben_documento_tipo: string;
  ben_documento_numero: string;
  ben_sexo: string;
  ben_fecha_nacimiento: string;
  ben_calle: string;
  ben_puerta: string;
  ben_piso: string;
  ben_departamento: string;
  ben_codigo_postal: string;
  ben_celular: string;
}

export interface Beneficiario {
  Cuil: string;
  ApellidoNombre: string;
  Parentesco: number;
  parentesco_desc: string;
  Documento_Tipo: string;
  Documento_Numero: string;
  Sexo: string;
  Fecha_Nacimiento: string;
  Fecha_Alta: string;
  Fecha_Baja: string;
  Calle: string;
  Puerta: string;
  Piso: string;
  Departamento: string;
  Codigo_Postal: string;
  Localidad: string;
  Provincia: string;
  Celular: string;
  Telefono: string;
  Mail: string;
  Discapacitado: string;
  Situacion_Revista: string;
  EstadoCivil: string;
  Nacionalidad: string;
}

export interface ColumnaExportable {
  key: string;
  label: string;
  selected: boolean;
}

@Component({
  selector: 'app-reporte-beneficiarios',
  templateUrl: './reporte-beneficiarios.component.html',
  styleUrls: ['./reporte-beneficiarios.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ReporteBeneficiariosComponent implements OnInit {

  // Filtros
  filtroPeriodoDesde: string = '';
  filtroPeriodoHasta: string = '';
  filtroCuil: string = '';
  filtroCuit: string = '';
  filtroNombre: string = '';
  filtroMinBeneficiarios: number = 0;

  // Estado
  loading: boolean = false;
  loadingGrupoFamiliar: boolean = false;
  exportando: boolean = false;

  // Tabla principal
  displayedColumns: string[] = [
    'cuil_titular', 'nombre_titular', 'Cuit_Empleador', 'Razon_Social',
    'Periodo', 'RemosImp', 'total_beneficiarios', 'acciones'
  ];
  dataSource: Titular[] = [];

  // Paginacion
  pageSize: number = 1000;
  pageIndex: number = 0;
  totalRecords: number = 0;
  pageSizeOptions: number[] = [100, 500, 1000, 2000];

  // Grupo familiar expandido
  expandedElement: Titular | null = null;
  grupoFamiliarData: Beneficiario[] = [];
  expandedBeneficiario: Beneficiario | null = null;

  fecha_hoy: string;

  // Modal de exportacion
  columnasExportables: ColumnaExportable[] = [
    { key: 'cuil_titular', label: 'CUIL Titular', selected: true },
    { key: 'nombre_titular', label: 'Nombre', selected: true },
    { key: 'Cuit_Empleador', label: 'CUIT Empleador', selected: true },
    { key: 'Razon_Social', label: 'Empresa', selected: true },
    { key: 'Periodo', label: 'Periodo', selected: false },
    { key: 'RemosImp', label: 'Rem. Imponible', selected: false },
    { key: 'Cant_GrupoFamiliar', label: 'Grupo Familiar', selected: false },
    { key: 'Cant_Adherentes', label: 'Adherentes', selected: false },
    { key: 'total_beneficiarios', label: 'Total Beneficiarios', selected: true },
    // Campos de beneficiarios
    { key: 'ben_documento_tipo', label: 'Tipo Doc.', selected: false },
    { key: 'ben_documento_numero', label: 'Nro. Doc.', selected: false },
    { key: 'ben_sexo', label: 'Sexo', selected: false },
    { key: 'ben_fecha_nacimiento', label: 'Fecha Nac.', selected: false },
    { key: 'ben_calle', label: 'Calle', selected: false },
    { key: 'ben_puerta', label: 'Puerta', selected: false },
    { key: 'ben_piso', label: 'Piso', selected: false },
    { key: 'ben_departamento', label: 'Depto', selected: false },
    { key: 'ben_codigo_postal', label: 'CP', selected: false },
    { key: 'ben_celular', label: 'Celular', selected: false },
    { key: 'ben_seccional', label: 'Seccional', selected: false }
  ];
  previewData: Titular[] = [];

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
    public snackBar: MatSnackBar
  ) {
    this.fecha_hoy = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    // Inicializar periodos con valores por defecto
    this.filtroPeriodoDesde = '20/01';
    this.filtroPeriodoHasta = '25/12';
  }

  ngOnInit() {
    console.log('Reporte de Beneficiarios iniciado');
  }

  /**
   * Buscar titulares segun filtros
   */
  buscarTitulares() {
    if (!this.filtroPeriodoDesde || !this.filtroPeriodoHasta) {
      this.snackBar.open('Debe seleccionar un rango de periodos', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    this.pageIndex = 0;
    this.cargarPagina();
  }

  /**
   * Cargar pagina actual
   */
  private cargarPagina() {
    this.loading = true;
    this.dataSource = [];
    this.expandedElement = null;
    this.grupoFamiliarData = [];

    const page = this.pageIndex + 1;
    let url = `${environment.backendUrl}/api/reporte-beneficiarios/titulares?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}&page=${page}&pageSize=${this.pageSize}`;

    if (this.filtroCuil && this.filtroCuil.trim() !== '') {
      url += `&cuil=${this.filtroCuil}`;
    }
    if (this.filtroCuit && this.filtroCuit.trim() !== '') {
      url += `&cuit=${this.filtroCuit}`;
    }
    if (this.filtroNombre && this.filtroNombre.trim() !== '') {
      url += `&nombre=${encodeURIComponent(this.filtroNombre)}`;
    }
    if (this.filtroMinBeneficiarios && this.filtroMinBeneficiarios !== 0) {
      url += `&minBenef=${this.filtroMinBeneficiarios}`;
    }

    console.log('Consultando:', url);

    this.http.get(url).subscribe(
      (response: any) => {
        if (response && response.data) {
          this.dataSource = response.data;
          this.totalRecords = response.total || 0;
          this.snackBar.open(`Se encontraron ${response.total} titulares`, 'Cerrar', {
            duration: 3000,
          });
        } else {
          this.dataSource = [];
          this.totalRecords = 0;
          this.snackBar.open('No se encontraron resultados', 'Cerrar', {
            duration: 3000,
          });
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error:', error);
        this.snackBar.open('Error al consultar el servidor', 'Cerrar', {
          duration: 5000,
        });
        this.loading = false;
      }
    );
  }

  /**
   * Expandir/colapsar fila para ver grupo familiar
   */
  toggleGrupoFamiliar(element: Titular) {
    if (this.expandedElement === element) {
      this.expandedElement = null;
      this.grupoFamiliarData = [];
      this.expandedBeneficiario = null;
    } else {
      this.expandedElement = element;
      this.expandedBeneficiario = null;
      this.cargarGrupoFamiliar(element.cuil_titular);
    }
  }

  /**
   * Expandir/colapsar detalle de un beneficiario
   */
  toggleBeneficiario(beneficiario: Beneficiario) {
    if (this.expandedBeneficiario === beneficiario) {
      this.expandedBeneficiario = null;
    } else {
      this.expandedBeneficiario = beneficiario;
    }
  }

  /**
   * Cargar grupo familiar de un titular
   */
  private cargarGrupoFamiliar(cuil: string) {
    this.loadingGrupoFamiliar = true;
    this.grupoFamiliarData = [];

    const url = `${environment.backendUrl}/api/reporte-beneficiarios/grupo-familiar/${cuil}`;
    console.log('Consultando grupo familiar:', url);

    this.http.get(url).subscribe(
      (response: any) => {
        if (response && response.length > 0) {
          this.grupoFamiliarData = response;
        } else {
          this.grupoFamiliarData = [];
        }
        this.loadingGrupoFamiliar = false;
      },
      (error) => {
        console.error('Error:', error);
        this.snackBar.open('Error al cargar grupo familiar', 'Cerrar', {
          duration: 3000,
        });
        this.loadingGrupoFamiliar = false;
      }
    );
  }

  /**
   * Manejar cambio de pagina
   */
  onPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.cargarPagina();
  }

  /**
   * Ir a pagina especifica
   */
  goToPage(page: number) {
    this.pageIndex = page;
    this.cargarPagina();
  }

  /**
   * Cambiar tamano de pagina
   */
  changePageSize(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 0;
    this.cargarPagina();
  }

  /**
   * Obtener numeros de pagina para paginador
   */
  getPageNumbers(): (number | string)[] {
    const totalPages = Math.ceil(this.totalRecords / this.pageSize);
    const currentPage = this.pageIndex;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage < 4) {
        for (let i = 0; i < 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        pages.push(0);
        pages.push('...');
        for (let i = totalPages - 5; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(0);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages - 1);
      }
    }

    return pages;
  }

  /**
   * Total de paginas
   */
  getTotalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  /**
   * Exportar a Excel
   */
  exportarExcel() {
    if (!this.dataSource || this.dataSource.length === 0) {
      this.snackBar.open('No hay datos para exportar', 'Cerrar', {
        duration: 3000,
      });
      return;
    }

    const dataParaExcel = this.dataSource.map(row => ({
      'CUIL Titular': row.cuil_titular,
      'Nombre': row.nombre_titular,
      'CUIT Empleador': row.Cuit_Empleador,
      'Empresa': row.Razon_Social,
      'Periodo': row.Periodo,
      'Remuneracion Imponible': row.RemosImp,
      'Grupo Familiar': row.Cant_GrupoFamiliar,
      'Adherentes': row.Cant_Adherentes,
      'Total Beneficiarios': row.total_beneficiarios
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataParaExcel);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Reporte Beneficiarios': worksheet },
      SheetNames: ['Reporte Beneficiarios']
    };

    const nombreArchivo = `Reporte_Beneficiarios_${this.filtroPeriodoDesde}_${this.filtroPeriodoHasta}_${this.fecha_hoy}.xlsx`;
    XLSX.writeFile(workbook, nombreArchivo, { bookType: 'xlsx', type: 'buffer' });

    this.snackBar.open('Archivo exportado exitosamente', 'Cerrar', {
      duration: 3000,
    });
  }

  /**
   * Abrir modal de exportacion con preview
   */
  abrirModalExportar() {
    this.previewData = this.dataSource.slice(0, 5);
  }

  /**
   * Obtener columnas seleccionadas para exportar
   */
  getColumnasSeleccionadas(): ColumnaExportable[] {
    return this.columnasExportables.filter(c => c.selected);
  }

  /**
   * Seleccionar todas las columnas
   */
  seleccionarTodas() {
    this.columnasExportables.forEach(c => c.selected = true);
  }

  /**
   * Deseleccionar todas las columnas
   */
  deseleccionarTodas() {
    this.columnasExportables.forEach(c => c.selected = false);
  }

  /**
   * Cargar TODOS los datos para exportacion (sin paginacion)
   */
  private cargarTodosLosDatos(): Promise<Titular[]> {
    return new Promise((resolve, reject) => {
      let url = `${environment.backendUrl}/api/reporte-beneficiarios/titulares/exportar?perdes=${this.filtroPeriodoDesde}&perhas=${this.filtroPeriodoHasta}`;

      if (this.filtroCuil && this.filtroCuil.trim() !== '') {
        url += `&cuil=${this.filtroCuil}`;
      }
      if (this.filtroCuit && this.filtroCuit.trim() !== '') {
        url += `&cuit=${this.filtroCuit}`;
      }
      if (this.filtroNombre && this.filtroNombre.trim() !== '') {
        url += `&nombre=${encodeURIComponent(this.filtroNombre)}`;
      }
      if (this.filtroMinBeneficiarios && this.filtroMinBeneficiarios !== 0) {
        url += `&minBenef=${this.filtroMinBeneficiarios}`;
      }

      console.log('Cargando todos los datos para exportar:', url);

      this.http.get(url).subscribe(
        (response: any) => resolve(response),
        (error) => reject(error)
      );
    });
  }

  /**
   * Exportar a Excel con columnas seleccionadas
   */
  exportarExcelConColumnas() {
    const columnasSeleccionadas = this.getColumnasSeleccionadas();

    if (columnasSeleccionadas.length === 0) {
      this.snackBar.open('Seleccione al menos una columna', 'Cerrar', { duration: 3000 });
      return;
    }

    this.exportando = true;
    this.snackBar.open('Cargando todos los registros para exportar...', 'Cerrar', { duration: 10000 });

    this.cargarTodosLosDatos().then((todosLosDatos: Titular[]) => {
      const dataParaExcel = todosLosDatos.map(row => {
        const obj: any = {};
        columnasSeleccionadas.forEach(col => {
          obj[col.label] = row[col.key];
        });
        return obj;
      });

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataParaExcel);
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Reporte Beneficiarios': worksheet },
        SheetNames: ['Reporte Beneficiarios']
      };

      const nombreArchivo = `Reporte_Beneficiarios_${this.filtroPeriodoDesde}_${this.filtroPeriodoHasta}_${this.fecha_hoy}.xlsx`;
      XLSX.writeFile(workbook, nombreArchivo, { bookType: 'xlsx', type: 'buffer' });

      // Cerrar modal
      document.getElementById('cerrarModalExportar').click();

      this.snackBar.open(`Archivo exportado exitosamente (${todosLosDatos.length} registros)`, 'Cerrar', {
        duration: 5000,
      });
      this.exportando = false;
    }).catch((error) => {
      console.error('Error al exportar:', error);
      this.snackBar.open('Error al cargar los datos para exportar', 'Cerrar', { duration: 5000 });
      this.exportando = false;
    });
  }

  /**
   * Exportar a PDF con columnas seleccionadas
   */
  exportarPdfConColumnas() {
    const columnasSeleccionadas = this.getColumnasSeleccionadas();

    if (columnasSeleccionadas.length === 0) {
      this.snackBar.open('Seleccione al menos una columna', 'Cerrar', { duration: 3000 });
      return;
    }

    // Generar tabla HTML para imprimir
    let tableHtml = '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 11px;">';

    // Header
    tableHtml += '<thead><tr style="background-color: #4472C4; color: white;">';
    columnasSeleccionadas.forEach(col => {
      tableHtml += `<th style="padding: 8px; text-align: left;">${col.label}</th>`;
    });
    tableHtml += '</tr></thead>';

    // Body
    tableHtml += '<tbody>';
    this.dataSource.forEach((row, index) => {
      const bgColor = index % 2 === 0 ? '#ffffff' : '#f2f2f2';
      tableHtml += `<tr style="background-color: ${bgColor};">`;
      columnasSeleccionadas.forEach(col => {
        let value = row[col.key];
        if (col.key === 'RemosImp' && value) {
          value = '$' + parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 });
        }
        tableHtml += `<td style="padding: 6px;">${value || '-'}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';

    // Crear ventana de impresion
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=100%');
    popupWin.document.open();
    popupWin.document.write(`
      <html>
        <head>
          <title>Reporte de Beneficiarios</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { font-size: 18px; color: #333; margin-bottom: 5px; }
            .info { font-size: 12px; color: #666; margin-bottom: 15px; }
            @media print {
              body { margin: 0; }
              @page { size: landscape; margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <h1>REPORTE DE BENEFICIARIOS</h1>
          <div class="info">
            Periodo: ${this.filtroPeriodoDesde} - ${this.filtroPeriodoHasta} |
            Total registros: ${this.dataSource.length} |
            Fecha: ${this.fecha_hoy}
          </div>
          ${tableHtml}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    popupWin.document.close();

    // Cerrar modal
    document.getElementById('cerrarModalExportar').click();
  }

  /**
   * Limpiar filtros
   */
  limpiarFiltros() {
    this.filtroPeriodoDesde = '20/01';
    this.filtroPeriodoHasta = '25/12';
    this.filtroCuil = '';
    this.filtroCuit = '';
    this.filtroNombre = '';
    this.filtroMinBeneficiarios = 0;
    this.dataSource = [];
    this.totalRecords = 0;
    this.pageIndex = 0;
    this.expandedElement = null;
    this.grupoFamiliarData = [];
  }
}
